import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { type ReactNode, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Form } from '@app/components/core/form/Form'
import { useInitiativeFormWatchAutosave } from '@app/pages/dashboard/initiative/shared/useInitiativeFormWatchAutosave'
import { useTranslations } from '@i18n/useLanguage'
import { enrich } from '@i18n/localizeHelper'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import {
  createLegalInfoFormSchema,
  type LegalInfoFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import {
  legalInfoDefaultValues,
  legalInfoToEditPayload,
} from '@app/pages/dashboard/initiative/shared/forms/legalInfo/legalInfoFormMappers'
import {
  LegalInfoFormFields,
  LegalInfoFormInitialSync,
} from '@app/pages/dashboard/initiative/shared/forms/legalInfo/LegalInfoFormFields'

const CURRENT_STEP = initiativeCreationSteps.legalInfo

export const LegalInfoStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const formValidation = useTranslations().app.form.validation

  const {
    potentialPlotStatus,
    potentialPlotAddress,
    permit,
    financingRequirementsConstruction,
    financingRequirementsPlanning,
    kvkRegistration,
  } = initiative

  const schema = useMemo(
    () => createLegalInfoFormSchema(enrich(formValidation.minLength, 6)),
    [formValidation.minLength],
  )

  const initialValues = useMemo(
    () =>
      legalInfoDefaultValues({
        potentialPlotStatus,
        potentialPlotAddress,
        permit,
        financingRequirementsConstruction,
        financingRequirementsPlanning,
        kvkRegistration,
      }),
    [
      potentialPlotStatus,
      potentialPlotAddress,
      permit,
      financingRequirementsConstruction,
      financingRequirementsPlanning,
      kvkRegistration,
    ],
  )

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Form<LegalInfoFormValues>
        schema={schema}
        defaultValues={initialValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <>
          <LegalInfoFormInitialSync initial={initialValues} />
          <LegalInfoOnboardingEffects
            initiativeId={initiative.id}
            completeStep={completeStep}
            uncompleteStep={uncompleteStep}
          />
          <LegalInfoFormFields />
        </>
      </Form>
    </CreationGuide>
  )
}

const LegalInfoOnboardingEffects = ({
  initiativeId,
  completeStep,
  uncompleteStep,
}: {
  initiativeId: number
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}): ReactNode => {
  const { formState, control } = useFormContext<LegalInfoFormValues>()
  useInitiativeFormWatchAutosave({
    initiativeId,
    control,
    toPayload: (v) => ({
      ...legalInfoToEditPayload(v),
      onboardingStep: CURRENT_STEP,
    }),
    watchDeps: (v) =>
      [
        v.potentialPlotStatus,
        v.potentialPlotAddress,
        v.permit,
        v.financingRequirementsConstruction,
        v.financingRequirementsPlanning,
        v.kvkRegistration,
      ] as const,
  })
  useEffect(() => {
    if (formState.isValid) {
      completeStep(CURRENT_STEP)
    } else {
      uncompleteStep(CURRENT_STEP)
    }
  }, [formState.isValid, completeStep, uncompleteStep])
  return null
}
